<script lang="ts" setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { TruckStatus } from "@/constants/truck";

const { t } = useI18n();

const isOpen = ref<boolean>(false);

const facilities = [
  {
    key: "rental",
    label: "maps.rental-points",
    icon: "mdi-key",
    bgClass: "bg-purple-darken-2",
    textClass: "text-purple-darken-2",
  },
  {
    key: "cross",
    label: "maps.cross-points",
    icon: "mdi-forklift",
    bgClass: "bg-cyan-darken-2",
    textClass: "text-cyan-darken-2",
  },
];

const truckStatuses = [
  {
    status: TruckStatus.Available,
    label: "trucks.status-a",
    badgeClass: "bg-green-lighten-5 border-green",
    textClass: "text-green",
  },
  {
    status: TruckStatus.WillBeAvailable,
    label: "trucks.status-w",
    badgeClass: "bg-indigo-lighten-5 border-indigo",
    textClass: "text-indigo",
  },
  {
    status: TruckStatus.InRoute,
    label: "trucks.status-r",
    badgeClass: "bg-amber-lighten-5 border-amber-darken-3",
    textClass: "text-amber-darken-3",
  },
  {
    status: TruckStatus.NotAvailable,
    label: "trucks.status-n",
    badgeClass: "bg-grey-lighten-4 border-grey-darken-1",
    textClass: "text-grey-darken-1",
  },
  {
    status: TruckStatus.OnHold,
    label: "trucks.status-h",
    badgeClass: "bg-red-lighten-5 border-red-lighten-1",
    textClass: "text-red-lighten-1",
  },
];
</script>

<template>
  <v-menu v-model="isOpen" :close-on-content-click="false" :width="250" location="top" offset="8">
    <template #activator="{ props: menuProps }">
      <div class="omni-map-legend text-grey-darken-3" v-bind="menuProps">
        <v-icon class="mr-2" icon="mdi-map-legend" size="18"></v-icon>
        <span class="font-weight-500">{{ t("maps.legend") }}</span>
      </div>
    </template>

    <v-card class="omni-map-legend__menu">
      <v-card-text>
        <div class="d-flex align-center justify-space-between mt-3">
          <span class="font-weight-500 text-grey-darken-3">{{ t("trucks.truck-status") }}</span>
          <v-btn
            color="grey-darken-2"
            density="comfortable"
            icon="mdi-close"
            size="md"
            variant="text"
            @click="isOpen = false"
          />
        </div>

        <div v-for="item in truckStatuses" :key="item.status" class="d-flex align-center mt-3 omni-map-legend__row">
          <div :class="[item.badgeClass, item.textClass]" class="omni-map-legend__badge">
            <v-icon size="16">mdi-truck</v-icon>
          </div>
          <span :class="item.textClass" class="font-weight-500 ml-3">{{ t(item.label) }}</span>
        </div>

        <div class="font-weight-500 text-grey-darken-3 mb-3 mt-5">{{ t("company.facilities") }}</div>

        <div v-for="facility in facilities" :key="facility.key" class="d-flex align-center mt-3 omni-map-legend__row">
          <div :class="facility.bgClass" class="omni-map-legend__facility-icon">
            <v-icon color="white" size="16">{{ facility.icon }}</v-icon>
          </div>
          <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t(facility.label) }}</span>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.omni-map-legend {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background-color: $white;
  border: 1px solid $grey-lighten-2;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: $grey-lighten-5;
  }

  &__menu {
    min-width: 240px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 24px;
    border-width: 2px;
    border-style: solid;
    border-radius: 8px;
  }

  &__facility-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
}
</style>
